import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { 
  FiMessageSquare, FiClock, FiCheckCircle, FiSearch, 
  FiPlus, FiEye, FiAlertCircle, FiX, FiPaperclip 
} from "react-icons/fi";
import { Link } from "react-router-dom";

function AllSupportTicket() {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null); // For View Modal

  const vendorId = localStorage.getItem("vendorId");

  useEffect(() => {
    fetchTickets();
  }, [vendorId]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${baseurl}getVendorSupportTickets/${vendorId}`);
      if (response.data.success) {
        setTickets(response.data.tickets);
        setFilteredTickets(response.data.tickets);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Search
  useEffect(() => {
    const results = tickets.filter(t => 
      t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.id.toString().includes(searchTerm)
    );
    setFilteredTickets(results);
  }, [searchTerm, tickets]);

  const stats = [
    { label: "Total Tickets", value: tickets.length, icon: <FiMessageSquare />, color: "blue" },
    { label: "Open Issues", value: tickets.filter(t => t.status === "open").length, icon: <FiAlertCircle />, color: "orange" },
    { label: "Resolved", value: tickets.filter(t => t.status === "resolved").length, icon: <FiCheckCircle />, color: "green" },
  ];

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper support-ticket-panel">
        <div className="main-content">
          
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h4 className="fw-bold mb-0">Support Center</h4>
              <p className="text-muted small mb-0">Manage your queries and technical assistance</p>
            </div>
            <Link to="/report-support-tickets" className="btn btn-primary d-flex align-items-center gap-2 shadow-sm">
              <FiPlus /> Raise New Ticket
            </Link>
          </div>

          <div className="row g-3 mb-4">
            {stats.map((stat, idx) => (
              <div className="col-12 col-md-4" key={idx}>
                <div className="stat-card shadow-sm border-0 card h-100">
                  <div className="card-body">
                    <div className={`icon-circle ${stat.color} mb-3`}>{stat.icon}</div>
                    <p className="text-muted small fw-bold text-uppercase mb-1">{stat.label}</p>
                    <h3 className="fw-bold mb-0">{stat.value}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="card-header bg-white py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-3">
              <h6 className="mb-0 fw-bold">Recent Tickets</h6>
              <div className="search-box">
                <FiSearch className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search Ticket ID or Subject..." 
                  className="form-control form-control-sm" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="card-body p-0">
              <div className="table-responsive">
                {loading ? (
                  <div className="p-5 text-center">Loading your requests...</div>
                ) : (
                  <table className="table table-hover align-middle mb-0 ticket-table">
                    <thead>
                      <tr>
                        <th>TICKET ID</th>
                        <th>SUBJECT</th>
                        <th>CATEGORY</th>
                        <th>PRIORITY</th>
                        <th>STATUS</th>
                        <th>DATE</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets.length > 0 ? (
                        filteredTickets.map((ticket) => (
                          <tr key={ticket.id}>
                            <td className="fw-bold text-muted">#TK-{ticket.id}</td>
                            <td>
                              <div className="fw-medium text-dark">{ticket.subject}</div>
                              {ticket.order_id && <small className="badge bg-secondary-subtle text-secondary fw-normal">Order: {ticket.order_id}</small>}
                            </td>
                            <td><span className="text-capitalize">{ticket.category}</span></td>
                            <td>
                              <span className={`priority-text ${ticket.priority}`}>
                                ● {ticket.priority}
                              </span>
                            </td>
                            <td>
                              <span className={`status-pill ${ticket.status}`}>
                                {ticket.status}
                              </span>
                            </td>
                            <td className="text-muted small">
                              {new Date(ticket.created_at).toLocaleDateString("en-GB")}
                            </td>
                            <td>
                              <button 
                                className="btn btn-light btn-sm rounded-circle shadow-sm"
                                onClick={() => setSelectedTicket(ticket)}
                              >
                                <FiEye className="text-primary" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="7" className="text-center p-5 text-muted">No tickets found.</td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* --- VIEW TICKET MODAL --- */}
        {selectedTicket && (
          <div className="custom-modal-overlay">
            <div className="custom-modal-content card shadow-lg border-0">
              <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0 fw-bold">Ticket Details [#TK-{selectedTicket.id}]</h5>
                <button className="btn btn-link text-dark p-0" onClick={() => setSelectedTicket(null)}><FiX size={24}/></button>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <label className="text-muted small fw-bold text-uppercase">Subject</label>
                  <p className="fw-bold text-dark h5">{selectedTicket.subject}</p>
                </div>
                
                <div className="row mb-4">
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase">Category</label>
                    <p className="text-capitalize">{selectedTicket.category}</p>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small fw-bold text-uppercase">Status</label>
                    <div><span className={`status-pill ${selectedTicket.status}`}>{selectedTicket.status}</span></div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-muted small fw-bold text-uppercase">Description</label>
                  <div className="p-3 bg-light rounded border text-secondary" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.description}
                  </div>
                </div>

                {selectedTicket.attachment_path && (
                  <div className="mb-3">
                    <label className="text-muted small fw-bold text-uppercase d-block mb-2"><FiPaperclip className="me-1"/> Attachment</label>
                    <a href={`${baseurl}${selectedTicket.attachment_path}`} target="_blank" rel="noreferrer">
                      <img 
                        src={`${baseurl}${selectedTicket.attachment_path}`} 
                        alt="attachment" 
                        className="img-thumbnail" 
                        style={{ maxHeight: '150px' }}
                      />
                    </a>
                  </div>
                )}
              </div>
              <div className="card-footer bg-white text-end py-3">
                <button className="btn btn-secondary px-4" onClick={() => setSelectedTicket(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </main>

      <style>{`
        .support-ticket-panel { background-color: #f4f7fa; min-height: 100vh; padding: 1.5rem; }
        .stat-card { border-radius: 12px; }
        .icon-circle { width: 45px; height: 45px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
        .icon-circle.blue { background: #e0f2fe; color: #0369a1; }
        .icon-circle.orange { background: #ffedd5; color: #9a3412; }
        .icon-circle.green { background: #dcfce7; color: #166534; }

        .search-box { position: relative; }
        .search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
        .search-box input { padding-left: 35px; width: 280px; border-radius: 8px; border: 1px solid #e2e8f0; }

        .ticket-table thead th { background: #f8fafc; color: #64748b; font-size: 0.75rem; font-weight: 700; padding: 1rem; border-bottom: 2px solid #edf2f7; }
        .ticket-table tbody td { padding: 1rem; border-bottom: 1px solid #f1f5f9; }

        .status-pill { padding: 4px 10px; border-radius: 6px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; }
        .status-pill.open { background: #dcfce7; color: #166534; }
        .status-pill.pending { background: #fef3c7; color: #92400e; }
        .status-pill.resolved { background: #e0f2fe; color: #0369a1; }

        .priority-text { font-size: 0.85rem; font-weight: 600; text-transform: capitalize; }
        .priority-text.high, .priority-text.urgent { color: #e11d48; }
        .priority-text.medium { color: #d97706; }
        .priority-text.low { color: #059669; }

        /* Modal Styles */
        .custom-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;
        }
        .custom-modal-content { width: 90%; max-width: 600px; max-height: 90vh; overflow-y: auto; border-radius: 12px; }

        .btn-primary { background: #232f3e; border: none; }
        .btn-primary:hover { background: #131921; }
      `}</style>
    </>
  );
}

export default AllSupportTicket;